import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 tracking-tight">
              OOPBuy<span className="text-primary-500">Spread</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Startseite
            </Link>
            <a href="/#kategorien" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Kategorien
            </a>
            <a href="/#ratgeber" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Einkaufsführer
            </a>
            <a href="/#faq" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              FAQ
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-full border-2 border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              OOPBuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy it now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Menü öffnen"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
              <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-4-line text-primary-600"></i>
              </span>
              Startseite
            </Link>
            <a href="/#kategorien" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
              <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-grid-line text-accent-600"></i>
              </span>
              Kategorien
            </a>
            <a href="/#ratgeber" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
              <span className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-book-open-line text-secondary-600"></i>
              </span>
              Einkaufsführer
            </a>
            <a href="/#faq" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
              <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-question-line text-accent-600"></i>
              </span>
              FAQ
            </a>

            <div className="my-2 mx-3 border-t border-background-200"></div>
            <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Kategorien</p>

            {categories.map((cat, idx) => (
              <a
                key={idx}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                  <img src={cat.thumb} alt={cat.name} className="w-full h-full object-cover" />
                </div>
                <span className="flex-1 whitespace-nowrap">{cat.name}</span>
                <i className="ri-external-link-line text-foreground-300 text-xs"></i>
              </a>
            ))}

            <div className="my-2 mx-3 border-t border-background-200"></div>

            <div className="flex gap-2 px-3 pt-2 pb-1">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex-1 text-center px-4 py-3 text-sm font-semibold rounded-full border-2 border-primary-500 text-primary-600 whitespace-nowrap cursor-pointer">OOPBuy Spreadsheet</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex-1 text-center px-4 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 whitespace-nowrap cursor-pointer">Buy it now</a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}